import useCurrentMerchantUser from 'hooks/useCurrentMerchantUser'
import {
  usePermissionReason,
  usePermissionResourceLabel,
  useRbacManageDisabled,
} from 'hooks/useRbacNavCheck'
import { buildTranslate } from 'locales'

const t = buildTranslate('order_ratings.settings.edit_permission')

export type SettingsEditPermission = {
  // True when the viewer may NOT change anything on the Settings tab. Named for
  // the disabled state, not the capability, because that is what every consumer
  // feeds straight into a control's `disabled`.
  editDisabled: boolean
  // True once `merchant_users/me` has landed, so `editDisabled` reflects the
  // viewer's own permissions rather than AbilityProvider's boot-time
  // Login-only placeholder. `editDisabled` alone cannot tell "still resolving"
  // from "denied" — it reads true for both — and `/order_feedback` is a plain
  // <Route>, so nothing upstream waits for the merchant user the way
  // ProtectedRoute does.
  //
  // Gate the BANNER on this, never a control: a control must stay disabled
  // while the answer is unknown (fail closed), but claiming a permission
  // problem before one is known is wrong.
  permissionResolved: boolean
  // The hover/focus tooltip reason, defined only while `editDisabled` AND both
  // RBAC flags are on. Fed to PermissionTooltip, never to a control's
  // `disabled` — those two are deliberately separate so an in-flight save is
  // never explained as a permissions problem.
  permissionReason: string | undefined
  // The live, human-readable permission name for the pane-level banner's body
  // copy. Always resolved (falls back to the curated literal), because the
  // banner is body copy and must read correctly with every flag off.
  permissionLabel: string
}

// FR-60's single gate for the Order feedback Settings tab: everything editable
// under it requires `manage MerchantSettings`.
//
// `useRbacManageDisabled`, not a hand-rolled `ability.can`: it is the repo's
// write gate, mirrors the server's `rbac_authorize!('manage',
// 'merchant_settings')` on `PATCH /merchants/:id`, and has no `rbacEnabled`
// branch because 'manage' is already the legacy threshold too. Presentation
// only — never a reason to drop the server-side authorize.
//
// NO admin check anywhere: `merchant_settings` is a "configure this thing"
// permission, and admin is reserved for creating users.
export const useSettingsEditPermission = (): SettingsEditPermission => {
  const editDisabled = useRbacManageDisabled('MerchantSettings')

  // The same input AbilityProvider builds the ability from, so this flips in
  // the same render the viewer's real permissions arrive in.
  const permissionResolved = Boolean(useCurrentMerchantUser())

  // The shared tooltip wording, which resolves only while BOTH
  // `role-based-access-control` and `rbac-permission-tooltips` are on.
  //
  // Deliberately NOT backstopped by a second, unflagged `formatPermissionReason`
  // call: that would opt this one control out of the `rbac-permission-tooltips`
  // kill switch, so flipping the flag off fleet-wide would suppress every other
  // tooltip but not this one. The pane-level banner is the unconditional
  // explanation, and it is body copy that no flag gates.
  const permissionReason = usePermissionReason('MerchantSettings', editDisabled)

  // Deliberately NOT usePermissionLabel: that one is gated behind the
  // tooltip-presentation flag and has no business gating page body copy.
  const liveLabel = usePermissionResourceLabel('MerchantSettings')
  const permissionLabel = liveLabel ?? t('label_fallback')

  return {
    editDisabled,
    permissionResolved,
    permissionReason,
    permissionLabel,
  }
}
